import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, Sparkles, X } from "lucide-react";
import { ALL_FOILS, FOIL_SECTIONS, STANDARD_FOILS, familyOf, findFoil, standardLabel } from "../lib/foilChart";
import { FoilOnPaper } from "./foil/FoilPairings";

export const FOIL_RECOMMEND = "Kolt recommends";

// "Choose foil color" picker — shown on products with `foil_chart_enabled`. Optional: the
// default hands the choice to our prepress team at proof stage.
export function FoilCodePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("all");
  const selected = findFoil(value);
  const query = q.trim().toLowerCase();
  const list = useMemo(() => ALL_FOILS.filter((f) => (family === "all" || f.family === family) && (!query || f.code.toLowerCase().includes(query))), [family, query]);

  return (
    <div className="mt-5" data-testid="foil-code-picker">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="overline">Foil color</div>
        <Link to={`/tools/foil-color-chart${selected ? `?foil=${encodeURIComponent(selected.code)}` : ""}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 hover:text-kolt-orange" data-testid="foil-picker-chart-link">Full chart <ArrowUpRight className="w-3 h-3" /></Link>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => { onChange(""); setOpen(false); }} className={`text-left border p-3 transition-colors ${!selected ? "border-kolt-slate-dark bg-kolt-slate-dark text-white" : "border-slate-300 bg-white hover:border-[#B87333]"}`} data-testid="foil-picker-recommend">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase font-bold"><Sparkles className="w-3.5 h-3.5 text-[#B87333]" /> {FOIL_RECOMMEND}</div>
          <div className={`text-[11px] mt-1 leading-snug ${!selected ? "text-white/70" : "text-slate-500"}`}>Our prepress team suggests a foil to match your artwork at proof stage.</div>
        </button>
        <button type="button" onClick={() => setOpen((o) => !o)} className={`text-left border p-3 transition-colors ${selected ? "border-[#B87333] bg-white ring-2 ring-[#B87333]/30" : "border-slate-300 bg-white hover:border-[#B87333]"}`} aria-expanded={open} data-testid="foil-picker-choose">
          {selected ? (
            <div className="flex items-center gap-3">
              <img src={selected.img} alt={`Foil ${selected.code}`} className="w-16 h-6 object-cover border border-slate-200" />
              <div>
                <div className="font-mono text-[12px] tracking-[0.14em] uppercase font-black text-kolt-slate-dark" data-testid="foil-picker-selected-code">{selected.code}</div>
                <div className="text-[10px] text-slate-500">{standardLabel(selected.code) || familyOf(selected.family).short} · tap to change</div>
              </div>
            </div>
          ) : (
            <>
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase font-bold text-kolt-slate-dark">Choose a foil code</div>
              <div className="text-[11px] text-slate-500 mt-1 leading-snug">Pick from {ALL_FOILS.length} metallic, pigment and holographic foils.</div>
            </>
          )}
        </button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="foil-picker-standards">
        <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-slate-500 mr-1">House standards</span>
        {STANDARD_FOILS.map(([code, label]) => {
          const f = findFoil(code);
          return f ? (
            <button key={code} type="button" onClick={() => { onChange(code); setOpen(false); }} className={`inline-flex items-center gap-1.5 border pl-0.5 pr-2 py-0.5 text-[10px] font-bold text-kolt-slate-dark transition-colors ${value === code ? "border-[#B87333] bg-[#B87333]/10" : "border-slate-300 hover:border-[#B87333]"}`} data-testid={`foil-picker-standard-${code}`}>
              <img src={f.img} alt="" className="w-7 h-4 object-cover" /> {label} · {code}
            </button>
          ) : null;
        })}
      </div>

      {open && (
        <div className="mt-2 border border-slate-300 bg-white p-3" data-testid="foil-picker-panel">
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a code — 120, HU941…" className="w-full border border-slate-300 focus:border-[#B87333] outline-none pl-8 pr-2 py-2 font-mono text-xs" data-testid="foil-picker-search" aria-label="Search foil codes" />
            </div>
            <button type="button" onClick={() => setOpen(false)} className="p-2 text-slate-400 hover:text-kolt-slate-dark" aria-label="Close foil picker" data-testid="foil-picker-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2" data-testid="foil-picker-families">
            {[["all", "All"], ...FOIL_SECTIONS.map((s) => [s.id, familyOf(s.id).short])].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setFamily(id)} className={`whitespace-nowrap font-mono text-[9px] tracking-[0.16em] uppercase font-bold px-2 py-1 border ${family === id ? "bg-kolt-slate-dark text-white border-kolt-slate-dark" : "border-slate-300 text-slate-600 hover:border-[#B87333]"}`} data-testid={`foil-picker-family-${id}`}>{label}</button>
            ))}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-64 overflow-y-auto pr-1" data-testid="foil-picker-grid">
            {list.map((f) => (
              <button key={f.code} type="button" onClick={() => { onChange(f.code); setOpen(false); }} className={`group text-left border transition-colors ${value === f.code ? "border-[#B87333]" : "border-slate-200 hover:border-[#B87333]"}`} data-testid={`foil-picker-option-${f.code.toLowerCase().replace(/\s+/g, "-")}`}>
                <img src={f.img} alt={`Foil ${f.code}`} loading="lazy" className="w-full aspect-[300/110] object-cover" />
                <div className="px-1.5 py-1 font-mono text-[9px] tracking-[0.12em] uppercase font-bold text-kolt-slate-dark truncate">{f.code}</div>
              </button>
            ))}
            {!list.length && <div className="col-span-full text-xs text-slate-500 py-4 text-center" data-testid="foil-picker-empty">No foil matches “{q}”.</div>}
          </div>
        </div>
      )}

      {selected && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-600" data-testid="foil-picker-preview">
          <FoilOnPaper foil={selected} paperHex="#1E1E1F" className="w-8 h-8 border border-slate-300" />
          <FoilOnPaper foil={selected} paperHex="#F7F6F2" className="w-8 h-8 border border-slate-300" />
          <span>Foil <b>{selected.code}</b> will be stamped as specified — one die per foil color.</span>
        </div>
      )}
    </div>
  );
}
